import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { useRejectQueueItemMutation } from '@/store/api/services/marketing/queue';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import type { QueueItem } from '@/types';

const RejectQueueItem = ({ hide, props }: ModalChildrenProps) => {
  const item = props?.data as QueueItem;
  const [reason, setReason] = useState('');
  const [rejectQueueItem, { isLoading }] = useRejectQueueItemMutation();

  if (item?.status !== 'pending') {
    return (
      <>
        <DialogContent sx={{ my: 2 }}>
          <Typography variant="h6">Cannot Reject</Typography>
          <Typography mt={1} variant="body1" color="text.secondary">
            Only <b>Pending</b> (Awaiting Publishing) queue items can be rejected. This item has
            status <b>{item?.status}</b>.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button fullWidth onClick={hide}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  const handleSubmit = async () => {
    try {
      await rejectQueueItem({ id: item.id, reason }).unwrap();
      hide();
      toast.success(`Queue item for location ${item.location_id} rejected.`);
    } catch (_err) {
      const meta = (_err as { data?: { meta?: string } })?.data?.meta;
      toast.error(meta ?? 'Failed to reject queue item.');
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="h6">Reject Queue Item</Typography>
        <Typography mt={1} mb={1} variant="body1">
          Rejecting the queued <b>{item?.action}</b> for location{' '}
          <b>{item?.location_name ?? item?.location_id}</b>. Please provide a reason.
        </Typography>
        <TextareaAutosize
          minRows={2}
          maxRows={3}
          placeholder="Enter reason for rejection..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{
            width: '100%',
            resize: 'vertical',
            padding: '12px 14px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            backgroundColor: 'transparent',
            outline: 'none',
            cursor: 'text',
          }}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!reason.trim()}
        >
          Reject
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default RejectQueueItem;
